export type CommandPaletteIndexKind = 'session' | 'smart_collection' | 'trove';

export interface CommandPaletteIndexEntry {
  id: string;
  kind: CommandPaletteIndexKind;
  label: string;
  subtitle?: string;
  path: string;
  keywords?: string;
  updatedAt: number;
}

export interface CommandPaletteIndexCache {
  aid: string;
  lastSyncAt: number;
  items: CommandPaletteIndexEntry[];
}

const DB_NAME = 'trove-command-palette';
const DB_VERSION = 1;
const STORE = 'index';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function itemKey(item: CommandPaletteIndexEntry): string {
  return `${item.kind}:${item.id}`;
}

export async function readCommandPaletteIndexCache(aid: string): Promise<CommandPaletteIndexCache | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(aid);
      req.onsuccess = () => resolve((req.result as CommandPaletteIndexCache | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function writeCommandPaletteIndexCache(cache: CommandPaletteIndexCache): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(cache, cache.aid);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore persistence errors */
  }
}

export function mergeCommandPaletteIndex(
  existing: CommandPaletteIndexEntry[],
  delta: CommandPaletteIndexEntry[],
  full: boolean
): CommandPaletteIndexEntry[] {
  if (full) return [...delta];
  const map = new Map<string, CommandPaletteIndexEntry>();
  for (const item of existing) map.set(itemKey(item), item);
  for (const item of delta) map.set(itemKey(item), item);
  return Array.from(map.values());
}
